import { useEffect, useRef, RefObject } from 'react'

type SliderEntry = {
  el: HTMLElement
  goPrev: () => void
  goNext: () => void
}

const registry: SliderEntry[] = []
let listenerAdded = false

function handleKeyDown(e: KeyboardEvent) {
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) return

  const viewHeight = window.innerHeight
  const candidates = registry
    .map(({ el, goPrev, goNext }) => {
      const rect = el.getBoundingClientRect()
      const visibleTop = Math.max(rect.top, 0)
      const visibleBottom = Math.min(rect.bottom, viewHeight)
      const visibleHeight = Math.max(0, visibleBottom - visibleTop)
      return { goPrev, goNext, visibleHeight }
    })
    .filter((x) => x.visibleHeight > 50)

  const winner = candidates.sort((a, b) => b.visibleHeight - a.visibleHeight)[0]
  if (!winner) return

  e.preventDefault()
  if (e.key === 'ArrowLeft') winner.goPrev()
  else winner.goNext()
}

export function useSliderKeyboard(
  containerRef: RefObject<HTMLElement | null>,
  goPrev: () => void,
  goNext: () => void,
  enabled: boolean
) {
  const goPrevRef = useRef(goPrev)
  const goNextRef = useRef(goNext)
  goPrevRef.current = goPrev
  goNextRef.current = goNext

  useEffect(() => {
    if (!enabled) return
    const el = containerRef.current
    if (!el) return

    const entry: SliderEntry = {
      el,
      goPrev: () => goPrevRef.current(),
      goNext: () => goNextRef.current(),
    }
    registry.push(entry)

    if (!listenerAdded) {
      listenerAdded = true
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      const i = registry.indexOf(entry)
      if (i >= 0) registry.splice(i, 1)
      if (registry.length === 0 && listenerAdded) {
        document.removeEventListener('keydown', handleKeyDown)
        listenerAdded = false
      }
    }
  }, [containerRef, enabled])
}
